/**
 * Расширения журнала звонков со стороны CRM.
 *
 * Телефония про CRM не знает: она лишь показывает то, что дали её реестры.
 * Здесь живёт вся CRM-специфика — резолв uuid→id, карточки, действия.
 *
 * **Правило импортов**: отсюда берутся только реестры, типы и константы
 * телефонии. Её репозитории поднимут модуль в инстансе, где телефонии нет.
 */
import type { ReactNode } from 'react';

import logger from '@ayocore/platform/common/helpers/logger';
import { REPOSITORIES } from '@ayocore/platform/common/types/repositories.generated';
import { IconUserPlus } from '@tabler/icons-react';

import {
  registerTelephonyCallAction,
  registerTelephonySubject,
} from '@ayocore/telephony/client/extensions/registries';
import type {
  TelephonyCallRow,
  TelephonySubjectDescriptor,
  TelephonyUiHelpers,
} from '@ayocore/telephony/client/extensions/types';
import type { CallSubjectRef } from '@ayocore/telephony/common/types/call-subject';

import { leadRepository, partnerRepository } from '../rpc';
import { LeadView } from '../components/Leads/views/LeadView';
import { PartnerView } from '../components/Partners/views/PartnerView';
import { CreateLeadFromCall } from './CreateLeadFromCall';

/** Кэш резолва `entity_type:uuid → id` на время жизни клиента. */
const entityIdCache = new Map<string, number>();

/**
 * Открыть карточку CRM по ссылке из журнала.
 *
 * В связи лежит uuid — вьюхи же принимают числовой id, поэтому нужен резолв.
 * Не найденная карточка не роняет журнал: клиента могли удалить уже после
 * привязки, и это не повод ломать список звонков.
 */
async function openCrmEntity(
  ref: CallSubjectRef,
  helpers: TelephonyUiHelpers,
  lookup: (uuid: string) => Promise<{ id?: number | null } | null>,
  renderView: (entityId: number) => ReactNode
): Promise<void> {
  const cacheKey = `${ref.entityType}:${ref.entityUuid}`;

  try {
    const cached = entityIdCache.get(cacheKey);
    const entityId = cached ?? (await lookup(ref.entityUuid))?.id ?? null;

    if (!entityId) {
      logger.warn('[crm-telephony] Карточка не найдена', { ref });
      return;
    }

    entityIdCache.set(cacheKey, entityId);
    helpers.openDrawer(renderView(entityId));
  } catch (error) {
    logger.error('[crm-telephony] Не удалось открыть карточку', { ref, error });
  }
}

/** Как журнал показывает партнёра, к которому привязан звонок. */
export const partnerSubjectDescriptor: TelephonySubjectDescriptor = {
  entityType: REPOSITORIES.CRM_PARTNER,
  label: 'Партнёр',
  openEntity: (ref, helpers) =>
    openCrmEntity(
      ref,
      helpers,
      uuid => partnerRepository.findById(uuid),
      partnerId => <PartnerView partnerId={partnerId} isModal showEventList={false} />
    ),
};

/**
 * Лид как клиент звонка.
 *
 * Появляется от действия «Создать лид»: звонок привязывается к тому, что из
 * него выросло, и больше не висит в «Нераспределённых».
 */
export const leadSubjectDescriptor: TelephonySubjectDescriptor = {
  entityType: REPOSITORIES.CRM_LEAD,
  label: 'Лид',
  openEntity: (ref, helpers) =>
    openCrmEntity(
      ref,
      helpers,
      uuid => leadRepository.findById(uuid),
      leadId => <LeadView leadId={leadId} isModal showEventList={false} />
    ),
};

/**
 * Звонок, с которого стоит завести лида.
 *
 * Только входящие без клиента: если звонивший уже в базе, работа идёт
 * в его карточке, а не в новом лиде. Внутренние вызовы к продажам
 * отношения не имеют.
 */
function isLeadWorthy(call: TelephonyCallRow): boolean {
  return call.direction === 'inbound' && !call.subject_label && Boolean(call.from_number);
}

/** Подключить расширения CRM к журналу звонков. */
export function registerCrmTelephonyClientExtensions(): void {
  registerTelephonySubject(partnerSubjectDescriptor);
  registerTelephonySubject(leadSubjectDescriptor);

  registerTelephonyCallAction({
    id: 'crm_create_lead',
    label: 'Создать лид',
    icon: IconUserPlus,
    isAvailable: isLeadWorthy,
    onSelect: (call, helpers) => {
      const surface = helpers.openModal(
        <CreateLeadFromCall call={call} onCreated={() => surface.close()} />,
        { title: 'Лид по звонку', size: 'lg' }
      );
    },
  });
}
